// The close button's other end: `shortlist-header.component.ng1.ts:200`'s `close()`.
//
// The header renders its close CTA only when it is handed a handler (see `ShortlistHeader`),
// so until now there was none and nothing was shown. This is that handler, and it is the
// first thing in the SPA to USE channel 2 rather than define it.
import { useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { IFrameEventType, iframeClient, isInIFrame } from '@woo-ext/protocol'

/**
 * `undefined` when there is no parent to tell, so the CTA stays hidden.
 *
 * ⚠️ THE HOOK DECIDES, NOT THE CALLER. `ShortlistHeader` already hides the CTA unless it is
 * both `inIframe` and given a handler; returning `undefined` outside a frame means those two
 * conditions cannot drift apart — a handler that is present but inert would render a close
 * button on `/chrome-extension/**` as a top-level tab, where clicking it does nothing at all.
 *
 * ⚠️ AND IT IS FIRE-AND-FORGET. Upstream's `close()` posts `CloseDialog` and returns; the
 * parent removes the frame, so there is nobody left to answer and no response type exists.
 * Awaiting one here would hang for the full timeout on every close.
 *
 * What is NOT ported yet: the two analytics calls that bracket it — `sendToActionTracking`
 * with the alert id, and the `OnShortlistCloseClicked` publisher event. Both need the
 * shortlist event bus, which arrives with the panel itself; the close must work before then,
 * and a missing analytics event is visible in the dashboards rather than silent.
 */
export function useCloseDialog(): (() => void) | undefined {
  const close = useCallback(() => {
    // `{}` rather than no payload: upstream sends `data: {}` with `tabId`/`frameId`
    // commented out, and the parent reads neither.
    iframeClient().send(IFrameEventType.CloseDialog, {})
  }, [])

  return isInIFrame() ? close : undefined
}

/**
 * `tabId` / `frameId` — the invoker the dialog should answer to.
 *
 * ⚠️ UPSTREAM READS THEM FROM `$stateParams` AND THEY ARE NEVER THERE. Neither is declared
 * on `chrome-extension.full-page-dialog` or on any state under it
 * (`full-page-dialog.state.config.ng1.ts` declares `url: ''` and nothing else), and
 * ui-router's `$stateParams` carries only declared params — so `invokerTabId` and
 * `invokerFrameId` are `undefined` on every dialog that has ever opened, and both messages
 * below have always travelled with an empty payload. The same shape as the commented-out
 * pair in `shortlist-header.component.ng1.ts`.
 *
 * Read from the query string here, which is where such a param WOULD arrive in html5Mode,
 * and omitted when absent — so the payload matches upstream's today and starts working the
 * day the content script sends them, without a second change here.
 */
function useInvoker(): { tabId?: string; frameId?: string } {
  const [params] = useSearchParams()
  const tabId = params.get('tabId')
  const frameId = params.get('frameId')
  return {
    ...(tabId === null ? {} : { tabId }),
    ...(frameId === null ? {} : { frameId }),
  }
}

/**
 * The full-page dialog's half of channel 2 — `full-page-dialog.component.ng1.ts`'s `$onInit`
 * and `onOverlayClicked`.
 *
 * ⚠️ `Ready` IS NOT A LIFECYCLE LOG. The content script opens the dialog frame and then has
 * to know it is there: upstream posts `Ready` from `$onInit`, and the parent is what sizes
 * and reveals the overlay. A dialog that never announces itself is a frame the parent keeps
 * hidden.
 *
 * Returns the overlay's close handler, or `undefined` outside a frame — see `useCloseDialog`
 * for why the hook decides rather than the caller.
 */
export function useDialogFrame(): (() => void) | undefined {
  const invoker = useInvoker()
  const framed = isInIFrame()

  useEffect(() => {
    if (!framed) return
    iframeClient().send(IFrameEventType.Ready, invoker)
    // Once per mount, as `$onInit` is. The invoker cannot change without a remount, and
    // re-announcing on every render would have the parent reveal the frame repeatedly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framed])

  const close = useCallback(() => {
    iframeClient().send(IFrameEventType.CloseDialog, invoker)
  }, [invoker.tabId, invoker.frameId])

  return framed ? close : undefined
}
